// frontend/src/components/EarningsAnalytics.jsx
// ✅ PROFESSIONAL DESIGN: No emojis, USD-based royalties

import React from "react";
import { getIdentityHeaders } from '../lib/identityGoogle';
import { API_BASE } from "../lib/api";

const api = (path) => (API_BASE ? `${API_BASE}${path}` : path);

const brand = {
  purple: "#6D5EF9",
  purpleDark: "#5B4EE8",
  purpleTint: "#F3F0FF",
  purpleBorder: "#E7E2FF",
  neutralText: "#111827",
  neutralBorder: "#E5E7EB",
};

// Purple gradient palette for bars
const BAR_COLORS = [
  "linear-gradient(90deg, #8B7FEB 0%, #6D5EF9 100%)",
  "linear-gradient(90deg, #A78BFA 0%, #8B5CF6 100%)",
  "linear-gradient(90deg, #C4B5FD 0%, #A78BFA 100%)",
  "linear-gradient(90deg, #7C3AED 0%, #6D28D9 100%)",
  "linear-gradient(90deg, #DDD6FE 0%, #C4B5FD 100%)",
  "linear-gradient(90deg, #6D28D9 0%, #5B21B6 100%)",
  "linear-gradient(90deg, #F3E8FF 0%, #E9D5FF 100%)",
  "linear-gradient(90deg, #9333EA 0%, #7E22CE 100%)",
];

export default function EarningsAnalytics() {
  const [loading, setLoading] = React.useState(true);
  const [period, setPeriod] = React.useState("30d");
  const [rows, setRows] = React.useState([]);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setError("");

        const res = await fetch(api(`/api/creator/royalties/by-style?period=${period}`), {
          headers: getIdentityHeaders(),
        });

        if (!res.ok) {
          throw new Error(`Failed to load: ${res.status}`);
        }

        const data = await res.json();
        
        if (alive) {
          const items = Array.isArray(data.items) ? data.items : [];
          
          const formatted = items.map(item => ({
            ...item,
            name: formatStyleName(item.name || item.id),
            earningsUSD: item.earningsUSD || 0,
          }));
          
          setRows(formatted);
        }
      } catch (e) {
        console.error("Failed to load earnings analytics:", e);
        if (alive) {
          setError(e.message || "Failed to load");
        }
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [period]);

  const totalEarnings = rows.reduce((s, r) => s + r.earningsUSD, 0);
  const maxEarnings = Math.max(...rows.map((r) => r.earningsUSD), 0.01);

  // Get rank badge for position
  const getRankBadge = (idx) => {
    if (idx === 0) return { text: "#1", class: "rank-first" };
    if (idx === 1) return { text: "#2", class: "rank-second" };
    if (idx === 2) return { text: "#3", class: "rank-third" };
    return { text: `#${idx + 1}`, class: "rank-other" };
  };

  return (
    <section className="cd-analytics-section">
      <div className="cd-card">
        <StyleTag />

        <div className="analytics-header">
          <h2 className="analytics-title">Earnings by Style</h2>
          <select
            className="period-select"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="all">All Time</option>
          </select>
        </div>

        {loading ? (
          <div className="loading">Loading earnings data...</div>
        ) : error ? (
          <div className="error-message">
            Error: {error}
            <div style={{ fontSize: '12px', marginTop: '8px', opacity: 0.8 }}>
              Make sure backend is running and /api/creator/royalties/by-style endpoint exists
            </div>
          </div>
        ) : rows.length === 0 ? (
          <div className="empty">No earnings data available for this period</div>
        ) : (
          <>
            <div className="summary-row">
              <div className="summary-item">
                <div className="summary-label">Period Earnings</div>
                <div className="summary-value">${totalEarnings.toFixed(2)}</div>
              </div>
              <div className="summary-item">
                <div className="summary-label">Active Styles</div>
                <div className="summary-value">{rows.length}</div>
              </div>
            </div>

            <div className="styles-list">
              {rows.map((row, idx) => {
                const pct = (row.earningsUSD / maxEarnings) * 100;
                const color = BAR_COLORS[idx % BAR_COLORS.length];
                const rankBadge = getRankBadge(idx);

                return (
                  <div key={row.id} className="style-row">
                    <div className="style-header">
                      <div className="style-info">
                        <span className={`rank-badge ${rankBadge.class}`}>
                          {rankBadge.text}
                        </span>
                        <span className="style-name">{row.name}</span>
                      </div>
                      <div className="style-earnings">
                        ${row.earningsUSD.toFixed(2)}
                      </div>
                    </div>

                    <div
                      className="earnings-bar"
                      style={{
                        background: color,
                        width: `${Math.max(pct, 5)}%`,
                      }}
                    />

                    <div className="style-meta">
                      <span className="meta-item">
                        {row.uses} use{row.uses !== 1 ? "s" : ""}
                      </span>
                      <span className="meta-divider">•</span>
                      <span className="meta-item">
                        ${(row.earningsUSD / row.uses).toFixed(3)} per use
                      </span>
                      <span className="royalty-rate">{row.ratePct}% rate</span>
                    </div>

                    <div className="analytics-chart">
                      <svg width="100%" height="60" viewBox="0 0 800 60" preserveAspectRatio="none">
                        <polyline
                          points={generateSparklinePoints(row.uses, 800, 60)}
                          fill="none"
                          stroke="#6D5EF9"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>

                    {row.updatedAt && (
                      <div className="last-earned">
                        Last activity: {timeAgo(row.updatedAt)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

function formatStyleName(style) {
  const normalized = String(style || "").toLowerCase().trim();
  if (!normalized || normalized === "default") {
    return "Standard Style";
  }
  return normalized
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function timeAgo(dateStr) {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = now - then;

  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function generateSparklinePoints(uses, width, height) {
  const points = Math.max(5, Math.min(uses, 20));
  const stepX = width / (points - 1);
  
  let coords = [];
  for (let i = 0; i < points; i++) {
    const x = i * stepX;
    const baseY = height / 2;
    const wave = Math.sin(i * 0.5) * (height * 0.3);
    const random = (Math.random() - 0.5) * (height * 0.2);
    const y = baseY + wave + random;
    coords.push(`${x},${Math.max(5, Math.min(height - 5, y))}`);
  }
  
  return coords.join(" ");
}

function StyleTag() {
  return (
    <style>{`
.analytics-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
  flex-wrap: wrap;
  gap: 10px;
}

.analytics-title {
  font-size: 20px;
  font-weight: 800;
  margin: 0;
  color: ${brand.neutralText};
}

.period-select {
  height: 38px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid ${brand.neutralBorder};
  background: white;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  color: ${brand.neutralText};
}

.period-select:hover {
  border-color: ${brand.purple};
}

.summary-row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 24px;
  padding: 16px;
  background: ${brand.purpleTint};
  border: 1px solid ${brand.purpleBorder};
  border-radius: 12px;
}

@media (max-width: 600px) {
  .summary-row {
    grid-template-columns: 1fr;
  }
}

.summary-item {
  text-align: center;
}

.summary-label {
  font-size: 13px;
  color: #6B7280;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}

.summary-value {
  font-size: 24px;
  font-weight: 900;
  color: ${brand.neutralText};
}

.styles-list {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.style-row {
  background: #FAFAFA;
  border: 1px solid ${brand.neutralBorder};
  border-radius: 12px;
  padding: 14px;
}

.style-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.style-info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.rank-badge {
  font-size: 14px;
  font-weight: 900;
  padding: 4px 10px;
  border-radius: 8px;
  min-width: 40px;
  text-align: center;
}

.rank-first {
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  color: #7C2D12;
}

.rank-second {
  background: linear-gradient(135deg, #C0C0C0 0%, #A8A8A8 100%);
  color: #374151;
}

.rank-third {
  background: linear-gradient(135deg, #CD7F32 0%, #A0522D 100%);
  color: #FFFFFF;
}

.rank-other {
  background: #F3F4F6;
  color: #6B7280;
}

.style-name {
  font-size: 16px;
  font-weight: 800;
  color: ${brand.neutralText};
}

.style-earnings {
  font-size: 18px;
  font-weight: 900;
  color: ${brand.purple};
}

.earnings-bar {
  height: 12px;
  border-radius: 6px;
  margin-bottom: 10px;
  transition: width 0.3s ease;
  min-width: 40px;
  box-shadow: 0 2px 8px rgba(109, 94, 249, 0.15);
}

.style-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #6B7280;
  font-weight: 600;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.meta-item {
  white-space: nowrap;
}

.meta-divider {
  color: #D1D5DB;
}

.royalty-rate {
  background: ${brand.purpleTint};
  color: ${brand.purple};
  padding: 4px 8px;
  border-radius: 6px;
  font-weight: 800;
  font-size: 12px;
  margin-left: auto;
}

.analytics-chart {
  margin-bottom: 8px;
  padding: 8px 0;
  border-top: 1px solid ${brand.neutralBorder};
}

.analytics-chart svg {
  display: block;
  width: 100%;
  height: 60px;
}

.last-earned {
  font-size: 12px;
  color: #9CA3AF;
  font-weight: 600;
}

.loading,
.empty {
  text-align: center;
  padding: 40px;
  color: #6B7280;
  font-weight: 600;
}

.error-message {
  text-align: center;
  padding: 24px;
  background: #FEF2F2;
  border: 1px solid #FCA5A5;
  border-radius: 12px;
  color: #991B1B;
  font-weight: 600;
}

@media (max-width: 640px) {
  .analytics-title {
    font-size: 18px;
  }
  
  .rank-badge {
    font-size: 12px;
    padding: 3px 8px;
    min-width: 35px;
  }
  
  .style-name {
    font-size: 14px;
  }
  
  .style-earnings {
    font-size: 16px;
  }
  
  .style-meta {
    font-size: 12px;
  }
}
    `}</style>
  );
}